import cn from 'classnames';
import { type FC } from 'react';
import { trackEvent } from 'dibs-tracking';
import { graphql, useFragment } from 'react-relay';
import { filterNulls } from 'dibs-ts-utils/exports/filterNulls';
import { dibsLazyClient } from 'dibs-lazy/exports/dibsLazyClient';
import { ClientSuspense } from 'dibs-elements/exports/ClientSuspense';
import authModalLoader from 'dibs-buyer-layout/src/utils/AuthModalLoader';
import { useServerVarsContext } from '../../../global/ServerVarsContext/ServerVarsContext';
import { usePdpRespMarketplaceLayoutSecondaryContext } from '../PdpRespMarketplaceLayout/PdpRespMarketplaceLayoutSecondaryContext';
import { usePdpSellerReviewsVariant } from '../../helpers/pdpSellerReviewsTestHelpers';
import { trackAbTestV2Variant } from 'dibs-ab-tests/exports/clientAbTestV2';
import { PDP_SELLER_REVIEWS } from '../../../constants/abTestsConstants';
import { VisibilityTracker } from 'dibs-visibility-tracker/exports/VisibilityTracker';

// components
import { RecentlyViewedItemsLazy } from 'dibs-recent-history/exports/RecentlyViewedItemsLazy';
import { SharedRespLinksBlock } from '../../../shared/SharedRespLinksBlock/SharedRespLinksBlock';
import { RecentlyViewedItemsProvider } from 'dibs-recent-history/exports/RecentlyViewedItemsProvider';
import { PdpSharedYMALItemsCarousel } from '../../shared/PdpSharedYMALItemsCarousel/PdpSharedYMALItemsCarousel';
import { PdpSharedEditorialArticles } from '../../shared/PdpSharedEditorialArticles/PdpSharedEditorialArticles';
import { PdpSharedSellerItemsCarousel } from '../../shared/PdpSharedSellerItemsCarousel/PdpSharedSellerItemsCarousel';
import { PdpRespMarketplacePersonalizedCarouselLazy } from '../PdpRespMarketplacePersonalizedCarouselLazy/PdpRespMarketplacePersonalizedCarouselLazy';
import { PdpSharedVisuallySimilarItemsCarouselLazy } from '../../shared/PdpSharedVisuallySimilarItemsCarousel/PdpSharedVisuallySimilarItemsCarouselLazy';
import { PdpSharedFeaturedRelatedItemsCarouselLazy } from '../../PdpFeaturedRelatedItemsCarousel/PdpSharedFeaturedRelatedItemsCarouselLazy';
import { PdpRespSellerInformation } from '../../PdpRespSellerInformation/PdpRespSellerInformation';
import PdpSharedEstimateSimilarSoldSectionLoader from '../../PdpSharedEstimateSimilarSold/PdpSharedEstimateSimilarSoldSectionLoader';
import { useShowPdpEstimateSimilarSoldSection } from '../../hooks/usePdpEstimateSimilarSold';

// types
import { type PdpRespMarketplaceContainerSecondary_viewer$key as Viewer } from './__generated__/PdpRespMarketplaceContainerSecondary_viewer.graphql';

// styles
import dibsCss from 'dibs-css';
import styles from './PdpRespMarketplaceContainerSecondary.module.css';

const PdpRespEstimateSimilarSoldSection = dibsLazyClient(
    () =>
        import(
            /* webpackChunkName: "PdpRespEstimateSimilarSoldSection" */ '../../PdpRespEstimateSimilarSoldSection/PdpRespEstimateSimilarSoldSection'
        )
);

const viewerFragment = graphql`
    fragment PdpRespMarketplaceContainerSecondary_viewer on Viewer {
        ...SharedRespLinksBlock_viewer
        ...PdpSharedYMALItemsCarousel_viewer
        ...PdpSharedSellerItemsCarousel_viewer
        ...PdpSharedVisuallySimilarItemsCarouselLazy_viewer
        ...PdpRespMarketplacePersonalizedCarouselLazy_viewer
        ...PdpSharedFeaturedRelatedItemsCarouselLazy_viewer @include(if: $afterDisplayAndNotBot)
        ... @include(if: $isEstimateSimilarSoldVariant) {
            ...usePdpEstimateSimilarSold_viewer @include(if: $afterDisplay)
        }
        isWordpressArticlesEnabled: isEnabledByLocale(featureName: "WP_EDITORIAL")
        user(userId: $userId) @include(if: $fetchUser) {
            ...PdpSharedYMALItemsCarousel_user
            ...PdpSharedSellerItemsCarousel_user
            ...PdpSharedVisuallySimilarItemsCarouselLazy_user
            ...PdpRespMarketplacePersonalizedCarouselLazy_user
            ...PdpSharedFeaturedRelatedItemsCarouselLazy_user @include(if: $afterDisplayAndNotBot)
            ...PdpRespSellerInformation_user @include(if: $fetchSellerReviewsSection)
            ...PdpRespEstimateSimilarSoldSection_user @include(if: $isEstimateSimilarSoldVariant)
        }
        itemRead(itemIds: $itemIds) {
            ... on Item {
                serviceId
                internalBuyLinks {
                    ...SharedRespLinksBlock_links
                }
                storyTellingModule(shortTitles: true) {
                    tabs {
                        supplementaryEditorial {
                            articles {
                                __typename
                            }
                        }
                    }
                }
                seller {
                    ...PdpRespSellerInformation_seller @include(if: $fetchSellerReviewsSection)
                }
                ...SharedRespLinksBlock_item
                ...PdpSharedSellerItemsCarousel_item
                ...PdpSharedEditorialArticles_refactor_item
                ...PdpSharedVisuallySimilarItemsCarouselLazy_item
                ...PdpRespSellerInformation_item @include(if: $fetchSellerReviewsSection)
                ... @include(if: $isEstimateSimilarSoldVariant) {
                    ...PdpRespEstimateSimilarSoldSection_item @include(if: $afterDisplay)
                    ...usePdpEstimateSimilarSold_item @include(if: $afterDisplay)
                }
            }
        }
    }
`;

export const PdpRespMarketplaceContainerSecondary: FC<{
    viewer: Viewer;
}> = ({ viewer: viewerRef }) => {
    const { isTablet, isBot, isPdpEstimateSimilarSoldTestVariant } = useServerVarsContext();
    const isPdpSellerReviewsVariant = usePdpSellerReviewsVariant();

    const {
        currency,
        relayVars: { afterDisplay },
    } = usePdpRespMarketplaceLayoutSecondaryContext();
    const viewer = useFragment(viewerFragment, viewerRef);

    const { user = null, itemRead } = viewer;

    const item = itemRead?.[0];

    const showPdpEstimateSimilarSoldSection = useShowPdpEstimateSimilarSoldSection({
        viewer: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? viewer || null : null,
        item: afterDisplay && isPdpEstimateSimilarSoldTestVariant ? item || null : null,
        currency,
    });

    if (!item) {
        return null;
    }

    const hasEditorialArticles =
        !!item?.storyTellingModule?.tabs?.[0]?.supplementaryEditorial?.articles?.length;

    return (
        <>
            {afterDisplay &&
                isPdpEstimateSimilarSoldTestVariant &&
                showPdpEstimateSimilarSoldSection && (
                    <ClientSuspense fallback={<PdpSharedEstimateSimilarSoldSectionLoader />}>
                        <PdpRespEstimateSimilarSoldSection
                            user={user}
                            item={item}
                            currency={currency}
                        />
                    </ClientSuspense>
                )}
            <VisibilityTracker
                onVisibilityChange={({ isVisible }) => {
                    if (isVisible) {
                        trackAbTestV2Variant(PDP_SELLER_REVIEWS);
                    }
                }}
            />
            {afterDisplay && isPdpSellerReviewsVariant && item?.seller && (
                <PdpRespSellerInformation seller={item?.seller} user={user} item={item} />
            )}
            <div className={styles.printHide}>
                <PdpSharedSellerItemsCarousel
                    item={item}
                    user={user}
                    viewer={viewer}
                    currency={currency}
                />
                <PdpRespMarketplacePersonalizedCarouselLazy
                    user={user}
                    viewer={viewer}
                    currency={currency}
                    afterDisplay={!!afterDisplay}
                />
                <PdpSharedYMALItemsCarousel user={user} currency={currency} viewer={viewer} />
                {/* Featured Related Items */}
                <PdpSharedFeaturedRelatedItemsCarouselLazy
                    viewer={viewer}
                    user={user}
                    afterDisplay={afterDisplay}
                    currency={currency}
                />
                <PdpSharedVisuallySimilarItemsCarouselLazy
                    item={item}
                    user={user}
                    viewer={viewer}
                    currency={currency}
                    afterDisplay={!!afterDisplay}
                />
            </div>
            <RecentlyViewedItemsProvider
                maxItems={isTablet ? 10 : 12}
                itemsPerPage={isTablet ? 5 : 6}
                excludedItemIds={item.serviceId ? [item.serviceId] : null}
            >
                <RecentlyViewedItemsLazy
                    isBot={isBot}
                    itemsPerPage={5}
                    currency={currency}
                    authModalLoader={authModalLoader}
                    wrapperClassName={cn(dibsCss.mtLarge, dibsCss.pbXxlarge, {
                        [dibsCss.borderB]:
                            hasEditorialArticles && viewer.isWordpressArticlesEnabled,
                        [dibsCss.borderBSolid]:
                            hasEditorialArticles && viewer.isWordpressArticlesEnabled,
                        [dibsCss.borderDolphin]:
                            hasEditorialArticles && viewer.isWordpressArticlesEnabled,
                    })}
                />
            </RecentlyViewedItemsProvider>
            {viewer.isWordpressArticlesEnabled && <PdpSharedEditorialArticles item={item} />}
            <div className={styles.printHide}>
                <SharedRespLinksBlock
                    item={item}
                    viewer={viewer}
                    itemSearch={null}
                    onClick={() => {
                        trackEvent({
                            category: 'product',
                            action: 'more ways to browse link click',
                        });
                    }}
                    links={item.internalBuyLinks?.filter(filterNulls) || null}
                />
            </div>
        </>
    );
};
